import React from 'react'

function fmt(n) {
  n = Math.round(n)
  if (n >= 10000000) return '₹' + (n / 10000000).toFixed(1) + ' Cr'
  if (n >= 100000) return '₹' + (n / 100000).toFixed(1) + ' L'
  if (n >= 1000) return '₹' + n.toLocaleString('en-IN')
  return '₹' + n
}

const TYPE_COLORS = {
  life: '#3d4a5c', health: '#4a6741', vehicle: '#b8441a', home: '#7a5d1e', other: '#8a8278'
}

const FREQ_LABELS = {
  monthly: 'Monthly', quarterly: 'Quarterly', 'half-yearly': 'Half-Yearly', yearly: 'Yearly'
}

function daysUntilExpiry(expiry_date) {
  if (!expiry_date) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const exp = new Date(expiry_date)
  exp.setHours(0, 0, 0, 0)
  return Math.round((exp - today) / (1000 * 60 * 60 * 24))
}

function expiryColor(days) {
  if (days === null) return 'var(--text-muted)'
  if (days <= 30) return '#b8441a'
  if (days <= 90) return '#c9a84c'
  return '#4a6741'
}

export default function InsuranceDetail({ policy, payments, claims, onClose }) {
  if (!policy) return null

  const typeColor = TYPE_COLORS[policy.type] || '#8a8278'
  const days = daysUntilExpiry(policy.expiry_date)
  const expColor = expiryColor(days)

  const now = new Date()
  const last6 = []
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    const label = d.toLocaleString('en-IN', { month: 'short', year: '2-digit' })
    const pay = payments.find(p => p.payment_month === key)
    last6.push({ key, label, status: pay?.status || null })
  }

  const rowStyle = {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    padding: '0.5rem 0', borderBottom: '1px solid var(--surface-hdr)'
  }
  const keyStyle = {
    fontFamily: 'monospace', fontSize: '0.67rem', textTransform: 'uppercase',
    letterSpacing: '0.08em', color: 'var(--text-muted)', paddingRight: '1rem', flexShrink: 0
  }
  const valStyle = { fontSize: '0.85rem', color: 'var(--text)', textAlign: 'right', fontFamily: 'monospace' }

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
      style={{ position: 'fixed', inset: 0, background: 'var(--modal-backdrop)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
    >
      <div style={{ background: 'var(--surface)', borderRadius: '10px', border: '1px solid var(--border)', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.1rem 1.5rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-hdr)' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ display: 'inline-block', background: typeColor, color: '#fff', padding: '0.15rem 0.45rem', borderRadius: '3px', fontFamily: 'monospace', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: '700' }}>
                {policy.type}
              </span>
              <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.1rem', fontWeight: '700', color: 'var(--text)' }}>{policy.name}</span>
            </div>
            {policy.provider && (
              <div style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>{policy.provider}</div>
            )}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--text-muted)', lineHeight: 1, padding: '0.2rem' }}>✕</button>
        </div>

        <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

          {days !== null && (
            <div style={{ background: 'var(--surface-hdr)', borderRadius: '6px', padding: '0.85rem 1.1rem', borderLeft: `4px solid ${expColor}` }}>
              <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                Expiry / Renewal
              </div>
              <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.3rem', fontWeight: '700', color: expColor }}>
                {days <= 0 ? 'Expired' : `${days} days left`}
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.1rem' }}>
                {new Date(policy.expiry_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </div>
            </div>
          )}

          <div>
            <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '0.6rem', paddingBottom: '0.3rem', borderBottom: '1px solid var(--surface-hdr)' }}>
              Policy Details
            </div>
            {policy.policy_number && (
              <div style={rowStyle}>
                <span style={keyStyle}>Policy No.</span>
                <span style={valStyle}>{policy.policy_number}</span>
              </div>
            )}
            <div style={rowStyle}>
              <span style={keyStyle}>Premium</span>
              <span style={{ ...valStyle, color: '#c9a84c', fontWeight: '700' }}>
                {fmt(policy.premium_amount)}
                <span style={{ fontSize: '0.7rem', fontWeight: '400', color: 'var(--text-muted)' }}> / {FREQ_LABELS[policy.premium_frequency] || policy.premium_frequency}</span>
              </span>
            </div>
            {policy.due_day && (
              <div style={rowStyle}>
                <span style={keyStyle}>Due Day</span>
                <span style={valStyle}>{policy.due_day} of period</span>
              </div>
            )}
            {policy.sum_assured && (
              <div style={rowStyle}>
                <span style={keyStyle}>Sum Assured</span>
                <span style={{ ...valStyle, fontWeight: '700' }}>{fmt(policy.sum_assured)}</span>
              </div>
            )}
            {policy.start_date && (
              <div style={rowStyle}>
                <span style={keyStyle}>Start Date</span>
                <span style={valStyle}>{new Date(policy.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
              </div>
            )}
            {policy.nominee && (
              <div style={rowStyle}>
                <span style={keyStyle}>Nominee</span>
                <span style={valStyle}>{policy.nominee}</span>
              </div>
            )}
            {policy.notes && (
              <div style={{ ...rowStyle, borderBottom: 'none', flexDirection: 'column', gap: '0.3rem' }}>
                <span style={keyStyle}>Notes</span>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>{policy.notes}</span>
              </div>
            )}
          </div>

          <div>
            <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '0.75rem', paddingBottom: '0.3rem', borderBottom: '1px solid var(--surface-hdr)' }}>
              Premium Payment History (Last 6 Months)
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', height: '54px' }}>
              {last6.map(({ key, label, status }) => {
                const barColor = status === 'paid' ? '#4a6741' : status === 'na' ? 'var(--border)' : status === 'unpaid' ? '#b8441a' : 'var(--surface-hdr)'
                const barH = status === 'paid' ? 40 : status === 'unpaid' ? 28 : 16
                return (
                  <div key={key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.3rem', justifyContent: 'flex-end', height: '100%' }}>
                    <div style={{ width: '100%', height: `${barH}px`, background: barColor, borderRadius: '3px 3px 0 0', transition: 'height 0.3s' }} />
                    <div style={{ fontFamily: 'monospace', fontSize: '0.55rem', color: 'var(--text-muted)', textAlign: 'center', whiteSpace: 'nowrap' }}>{label}</div>
                  </div>
                )
              })}
            </div>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '0.6rem', flexWrap: 'wrap' }}>
              {[['#4a6741', 'Paid'], ['#b8441a', 'Unpaid'], ['var(--border)', 'N/A'], ['var(--surface-hdr)', 'No record']].map(([color, label]) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '2px', background: color, border: '1px solid var(--border)', flexShrink: 0 }} />
                  <span style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)' }}>{label}</span>
                </div>
              ))}
            </div>
          </div>

          {claims.length > 0 && (
            <div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '0.6rem', paddingBottom: '0.3rem', borderBottom: '1px solid var(--surface-hdr)' }}>
                Claims ({claims.length})
              </div>
              {claims.map((claim, i) => (
                <div key={claim.id} style={{ padding: '0.7rem 0.9rem', background: 'var(--surface-hdr)', borderRadius: '6px', marginBottom: i < claims.length - 1 ? '0.5rem' : 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.2rem' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: '600', color: 'var(--text)' }}>{claim.description || 'Claim'}</span>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: '700', color: '#c9a84c' }}>{fmt(claim.claim_amount || 0)}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    {claim.claim_date && (
                      <span style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)' }}>
                        {new Date(claim.claim_date).toLocaleDateString('en-IN')}
                      </span>
                    )}
                    {claim.status && (
                      <span style={{
                        fontFamily: 'monospace', fontSize: '0.6rem',
                        padding: '0.1rem 0.4rem', borderRadius: '3px',
                        background: claim.status === 'settled' ? 'rgba(74,103,65,0.12)' : claim.status === 'rejected' ? 'rgba(184,68,26,0.12)' : 'rgba(201,168,76,0.12)',
                        color: claim.status === 'settled' ? '#4a6741' : claim.status === 'rejected' ? '#b8441a' : '#c9a84c'
                      }}>
                        {claim.status}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>
      </div>
    </div>
  )
}
